import React, {useEffect, useState} from 'react'
import _ from 'lodash'
import classNames from 'classnames'
import dayjs from 'dayjs'
import {Link} from 'react-router-dom'
import {Formik} from 'formik'
import {Avatar, Button, Input, message, Modal, Select, Space} from 'antd'
import useTranslate from 'modules/local/useTranslate'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import getLinkToDetail from 'helpers/getLinkToDetail'
import getAvatar from 'helpers/getAvatar'
import getTitle from 'helpers/getTitle'
import {
    familyBook_addFamilyBookMemberVocation_Api,
    familyBook_deleteFamilyBookMemberVocation_Api,
    familyBook_updateFamilyBookMember_Api,
    familyBook_updateFamilyBookMemberVocation_Api,
    familyBookMember_getById_Api,
} from 'apis/familyBook'
import {LoadingOutlined} from '@ant-design/icons'
import {IoChevronForward, IoLibraryOutline, IoLockClosedOutline, IoPencilOutline} from 'react-icons/io5'
import {RiSearch2Line} from 'react-icons/ri'
import {BsPersonFill} from 'react-icons/bs'
import {FaPlus} from 'react-icons/fa6'
import {GiPrayer} from 'react-icons/gi'
import DrawDialog from 'components/drawer/dialog/DrawDialog'
import LookupField from 'components/LookupField'
import UserLookupComponent from 'components/UserLookupComponent'
import {FormActions, Section} from 'components/form/ModernForm'
import {computeAge} from '../functions/memberDisplay'
import {
    renderParishLookupOption,
    renderReligiousOrderLookupOption,
    renderSaintLookupOption
} from '../functions/lookupOptionRenderers'
import GenderIcon from './GenderIcon'
import PriestBadge from './PriestBadge'
import ReligiousBadge from './ReligiousBadge'
import AddRelativeModal from './AddRelativeModal'
import {InfoBanner} from './MomentRow'

// Thứ tự cố định 6 bí tích - luôn hiện đủ 6 ô (đã lãnh nhận: có ngày+giáo xứ, màu family; chưa
// lãnh nhận: ô "inactive" mờ, không giấu đi) để nhìn tổng quan biết ngay còn thiếu bí tích nào.
// Export để MyMemberCard (Hồ Sơ tab, FamilyBooks.js) dùng chung format - không tự chế bản song song.
export const ALL_SACRAMENT_TYPES = ['baptism', 'first_communion', 'confirmation', 'matrimony', 'holy_orders', 'anointing_of_the_sick']
export const SACRAMENT_LABELS = {
    baptism: 'sacrament_baptism',
    first_communion: 'sacrament_first_communion',
    confirmation: 'sacrament_confirmation',
    matrimony: 'sacrament_matrimony',
    holy_orders: 'sacrament_holy_orders',
    anointing_of_the_sick: 'sacrament_anointing_of_the_sick',
}

// Ơn Gọi Thánh Hiến (khấn dòng...) - TÁCH RIÊNG khỏi bí tích (không phải bí tích, xem
// family_book_member_vocations). Khác 6 ô bí tích (áp dụng cho MỌI thành viên nên luôn hiện đủ
// dạng grid cố định), vocation chỉ liên quan thiểu số thành viên là tu sĩ VÀ có thể lặp lại nhiều
// dòng cùng loại theo thời gian (chuyển dòng - transitus) - nên hiển thị dạng LIST (thêm/sửa/xoá
// từng dòng), không phải grid slot cố định như bí tích. Xem MemberDetailContent - chỉ hiện block
// này khi đã có dữ liệu hoặc đang có quyền sửa.
// "Rời dòng" KHÔNG phải 1 vocation_type riêng (từng cân nhắc rồi bỏ) - đóng lại 1 dòng khấn đang có
// bằng cách set `end_date` NGAY TRÊN row đó (nút "Rời Dòng" ở MemberDetailContent), không tạo row
// mới - phản ánh đúng bản chất "đóng lại", không phải "1 sự kiện loại khác".
export const ALL_VOCATION_TYPES = ['FIRST_PROFESSION', 'PERPETUAL_VOWS']
export const VOCATION_LABELS = {
    FIRST_PROFESSION: 'vocation_first_profession',
    PERPETUAL_VOWS: 'vocation_perpetual_vows',
}

// Field label + value - LUÔN hiện label, value trống thì hiện "Chưa có dữ liệu" (mờ) thay vì giấu
// cả field đi - mục đích xem tổng quan biết ngay còn thiếu thông tin gì để bổ sung.
export const InfoField = ({label, value, span, t}) => (
    <div className={classNames('flex flex-col gap-0.5 min-w-0', span && 'col-span-2')}>
        <span className="text-2xs font-semibold text-color-400 uppercase tracking-wider">{label}</span>
        {value
            ? <span className="text-sm font-medium text-color-000 break-words">{value}</span>
            : <span className="text-sm italic text-color-300">{t('not provided yet')}</span>
        }
    </div>
)

// Giống InfoField nhưng value bấm được thẳng (mailto:/tel:) - dùng cho contact_email/contact_phone
// (item.contact_email/contact_phone đã được BE resolve sẵn: ưu tiên email/SĐT tài khoản liên kết,
// fallback contact_email/contact_phone tự khai - xem FamilyBookMemberTransformer), MỤC ĐÍCH của 2
// field này là "để liên hệ" nên phải bấm gọi/gửi mail được ngay, không chỉ hiện text tĩnh như
// InfoField thường. Export để MyMemberCard (FamilyBooks.js) dùng lại - cùng 1 nguồn hiển thị.
// hidden: BE đã trả value=null vì người xem KHÔNG có quyền xem contact (canManageFamilyBookContact()
// ở FamilyBookMemberTransformer - xem item.can_manage_contact), KHÁC hẳn "chưa khai" - phải phân
// biệt rõ 2 case, không dùng chung "Chưa cung cấp" kẻo người xem tưởng nhầm là dữ liệu không tồn tại.
export const ContactField = ({label, value, href, span, hidden, t}) => (
    <div className={classNames('flex flex-col gap-0.5 min-w-0', span && 'col-span-2')}>
        <span className="text-2xs font-semibold text-color-400 uppercase tracking-wider">{label}</span>
        {value
            ? <a href={href} className="text-sm font-medium text-family-700 no-underline hover:underline break-words">{value}</a>
            : <span className="text-sm italic text-color-300">{t(hidden ? 'contact info hidden' : 'not provided yet')}</span>
        }
    </div>
)

// onClick: optional - DrawDialog (bọc ngoài khi editable, xem MemberDetailContent) tự inject qua
// cloneElement khi dùng làm `component` trigger. Không truyền thì y hệt hành vi cũ (MyMemberCard ở
// FamilyBooks.js dùng bản chỉ-xem, không đổi gì bên đó).
export const SacramentSlot = ({type, entry, onClick, t}) => (
    <div
        onClick={onClick}
        className={classNames(
            'p-2.5 rounded-lg border transition-all',
            entry ? 'border-family-200 bg-family-50' : 'border-dashed border-color-200 bg-color-50 opacity-60',
            onClick && 'cursor-pointer hover:shadow-md hover:opacity-100'
        )}>
        <div className={classNames('text-xs font-bold leading-snug', entry ? 'text-family-700' : 'text-color-300')}>
            {t(SACRAMENT_LABELS[type])}
        </div>
        {entry ? (
            <>
                <div className="text-2xs text-color-500 mt-0.5">{entry.eff_date ? dayjs(entry.eff_date).format('DD/MM/YYYY') : ''}</div>
                {entry.parish_name && <div className="text-2xs text-color-400">{entry.parish_name}</div>}
            </>
        ) : (
            <div className="text-2xs text-color-300 mt-0.5">{t('not received yet')}</div>
        )}
    </div>
)

// 1 dòng Ơn Gọi Thánh Hiến trong list (KHÁC SacramentSlot - đó là ô cố định trong grid 6 loại, còn
// đây là 1 dòng trong list có thể nhiều/ít tuỳ người). onClick (nếu có, do DrawDialog inject qua
// cloneElement - xem MemberDetailContent) mở form sửa. `actions` - node phụ bên phải (vd nút "Rời
// Dòng") - đặt NGOÀI vùng bấm sửa, tự chịu trách nhiệm stopPropagation nếu cần.
export const VocationRow = ({entry, onClick, actions, t}) => (
    <div
        onClick={onClick}
        className={classNames(
            'p-2.5 flex items-center gap-2 rounded-lg border border-family-200 bg-family-50 transition-all',
            onClick && 'cursor-pointer hover:shadow-md'
        )}>
        <GiPrayer size={16} className="text-family-500 flex-shrink-0"/>
        <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-family-700 leading-snug">{t(VOCATION_LABELS[entry.vocation_type])}</div>
            <div className="text-2xs text-color-500 mt-0.5">{entry.eff_date ? dayjs(entry.eff_date).format('DD/MM/YYYY') : ''}</div>
            {entry.religious_order_name && <div className="text-2xs text-color-400 truncate">{entry.religious_order_name}</div>}
            {/* end_date - đã rời dòng này, xem giải thích ở ALL_VOCATION_TYPES phía trên. */}
            {entry.end_date && (
                <div className="text-2xs text-red-400 italic mt-0.5">
                    {t('left on date', {date: dayjs(entry.end_date).format('DD/MM/YYYY')})}
                </div>
            )}
        </div>
        {actions}
    </div>
)

// Sửa nhanh 1 bí tích - BE đã sẵn cả rồi (FamilyBookMember::edit() tự parse field tiền tố
// "{type}_eff_date"/"{type}_parish_id"/"{type}_officiant_user_id"/... cho cả 6 loại bí tích, gọi
// CHUNG qua familyBook_updateFamilyBookMember_Api, không cần API riêng) - chỉ thiếu UI để nhập.
// Đủ field BE hỗ trợ: ngày, giáo xứ, mã sổ sách (source_idcode), chủ sự (officiant - thường là linh
// mục), người đỡ đầu (participant) + tên thánh người đỡ đầu (chỉ áp dụng khi gõ tự do, không có tài
// khoản - có tài khoản thì tên thánh suy thẳng từ hồ sơ họ, xem FamilyBookMemberSacramentTransformerBasic).
// Officiant/participant dùng UserLookupComponent - CHÍNH XÁC theo email/mã định danh, cùng convention
// đã dùng cho "Họ và tên" ở AddRelativeForm, không tự chế lookup mờ theo tên.
const SacramentEditForm = ({member, type, entry, onClose, onSuccess, t}) => {
    const {handleAsyncAction: doUpdate, loading} = useAsyncAction({
        apiInfo: familyBook_updateFamilyBookMember_Api,
        onSuccess: () => {
            message.success(t('sacrament updated'))
            onSuccess && onSuccess()
            onClose && onClose()
        },
        onError: () => message.error(t('error occurred')),
    })

    const initialValues = {
        eff_date: entry?.eff_date ? dayjs(entry.eff_date).format('YYYY-MM-DD') : '',
        source_idcode: entry?.source_idcode || '',
        // LookupField cần đúng shape {id, value} cho selectedOption - đúng convention đã dùng ở
        // EditFamilyBookModal.js, không truyền thẳng object transformer thô (field là "name", không
        // phải "value").
        parish: entry?.parish ? {id: entry.parish.id, value: entry.parish.name || entry.parish_name} : null,
        parish_name: entry?.parish_name || '',
        officiant_name: entry?.officiant_user?.name || entry?.officiant_name || '',
        officiant_user_id: entry?.officiant_user?.id || null,
        participant_name: entry?.participant_user?.name || entry?.participant_name || '',
        participant_user_id: entry?.participant_user?.id || null,
        participant_patron_saint: entry?.participant_patron_saint
            ? {id: entry.participant_patron_saint.id, value: entry.participant_patron_saint.name || entry?.participant_patron_saint_name}
            : null,
        participant_patron_saint_name: entry?.participant_patron_saint_name || '',
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values) => {
                doUpdate({
                    [`${type}_eff_date`]: values.eff_date || undefined,
                    [`${type}_source_idcode`]: values.source_idcode || undefined,
                    [`${type}_parish_id`]: values.parish?.id || undefined,
                    [`${type}_parish_name`]: values.parish?.value || values.parish_name || undefined,
                    [`${type}_officiant_user_id`]: values.officiant_user_id || undefined,
                    [`${type}_officiant_name`]: values.officiant_name || undefined,
                    [`${type}_participant_user_id`]: values.participant_user_id || undefined,
                    [`${type}_participant_name`]: values.participant_name || undefined,
                    [`${type}_participant_patron_saint_id`]: values.participant_patron_saint?.id || undefined,
                    [`${type}_participant_patron_saint_name`]: values.participant_patron_saint?.value || values.participant_patron_saint_name || undefined,
                }, {id: member.id})
            }}>
            {({values, setFieldValue, setValues, handleSubmit, isSubmitting}) => (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="font-bold text-lg text-color-000 uppercase tracking-wide text-center">
                        {t(SACRAMENT_LABELS[type])}
                    </div>

                    {/* native <input type="date"> qua antd Input - đúng convention đã chốt cho phân
                        hệ Sổ Gia Đình (trải nghiệm native thật trên iOS/Android), xem
                        AddRelativeForm trong FamilyBookDetail.js. */}
                    <Section title={t('sacrament date')} subtitle={t('optional')}>
                        <Input
                            type="date"
                            size="large"
                            className="w-full"
                            value={values.eff_date}
                            onChange={(e) => setFieldValue('eff_date', e.target.value)}
                        />
                    </Section>

                    <Section title={t('parish')} subtitle={t('optional')}>
                        <LookupField
                            title={t('choose parish')}
                            createIfNotExists={true}
                            defaultValue={values.parish_name}
                            selectedOption={values.parish}
                            apiPath="/lookup/parish/get-by-types"
                            name="sacrament_parish"
                            renderOption={renderParishLookupOption}
                            handleChange={(e) => setValues({...values, parish: e.target, parish_name: e.target.value})}
                        />
                    </Section>

                    <Section title={t('officiant')} subtitle={t('optional')}>
                        <Space.Compact style={{display: 'flex', width: '100%'}}>
                            <Input
                                size="large"
                                value={values.officiant_name}
                                onChange={(e) => setValues({...values, officiant_name: e.target.value, officiant_user_id: null})}
                                allowClear
                                placeholder={t('officiant placeholder')}
                                className="flex-1"
                            />
                            <UserLookupComponent
                                handleApply={(userInfo) => {
                                    if (userInfo?.id) setValues({...values, officiant_name: userInfo.name, officiant_user_id: userInfo.id})
                                }}>
                                <Button size="large" type="primary" icon={<RiSearch2Line size={14}/>} className="flex flex-center"/>
                            </UserLookupComponent>
                        </Space.Compact>
                    </Section>

                    {/* Nhãn theo TỪNG loại bí tích: hôn phối là "người làm chứng" (KHÔNG phải "đỡ
                        đầu" - 2 vai trò khác nhau trong Giáo Luật), 5 loại còn lại giữ "người đỡ
                        đầu" như cũ. Cùng field DB (participant_*), chỉ đổi nhãn hiển thị. */}
                    <Section
                        title={t(type === 'matrimony' ? 'participant_witness' : 'participant')}
                        subtitle={t(type === 'matrimony' ? 'participant_witness hint' : 'participant hint')}>
                        <div className="flex flex-col gap-2">
                            <Space.Compact style={{display: 'flex', width: '100%'}}>
                                <Input
                                    size="large"
                                    value={values.participant_name}
                                    onChange={(e) => setValues({...values, participant_name: e.target.value, participant_user_id: null})}
                                    allowClear
                                    placeholder={t(type === 'matrimony' ? 'participant_witness placeholder' : 'participant placeholder')}
                                    className="flex-1"
                                />
                                <UserLookupComponent
                                    handleApply={(userInfo) => {
                                        if (userInfo?.id) setValues({...values, participant_name: userInfo.name, participant_user_id: userInfo.id})
                                    }}>
                                    <Button size="large" type="primary" icon={<RiSearch2Line size={14}/>} className="flex flex-center"/>
                                </UserLookupComponent>
                            </Space.Compact>
                            {/* Tên thánh người đỡ đầu - chỉ cần nhập khi họ KHÔNG có tài khoản (gõ
                                tên tự do ở trên); có tài khoản thì tên thánh tự suy từ hồ sơ họ, ẩn
                                field này đi để khỏi hỏi thừa/lệch với hồ sơ thật của họ. */}
                            {!values.participant_user_id && (
                                <LookupField
                                    title={t('choose patron saint')}
                                    createIfNotExists={true}
                                    defaultValue={values.participant_patron_saint_name}
                                    selectedOption={values.participant_patron_saint}
                                    apiPath="/lookup/saint/get-by-types"
                                    name="participant_patron_saint"
                                    placeholder={t('participant patron saint placeholder')}
                                    renderOption={renderSaintLookupOption}
                                    handleChange={(e) => setValues({...values, participant_patron_saint: e.target, participant_patron_saint_name: e.target.value})}
                                />
                            )}
                        </div>
                    </Section>

                    <Section title={t('family book source code')} subtitle={t('optional')}>
                        <Input
                            size="large"
                            value={values.source_idcode}
                            onChange={(e) => setFieldValue('source_idcode', e.target.value)}
                        />
                    </Section>

                    <FormActions
                        onCancel={onClose}
                        submitText="save"
                        isSubmitting={isSubmitting || loading}
                        isValid={true}
                    />
                </form>
            )}
        </Formik>
    )
}

// Thêm mới/sửa 1 mốc Ơn Gọi Thánh Hiến - KHÁC SacramentEditForm ở trên: không dùng flat-field
// upsert-theo-type qua familyBook_updateFamilyBookMember_Api nữa, mà gọi API riêng theo id (add khi
// `entry` rỗng, update khi đã có entry.id) - vì 1 người có thể có NHIỀU dòng CÙNG vocation_type
// (chuyển dòng), xem 067_create_family_book_member_vocations.sql. `vocation_type` chỉ chọn được lúc
// THÊM MỚI; lúc SỬA thì giữ cố định, hiện dạng nhãn tĩnh. `end_date` (rời dòng) sửa được ở đây như
// field bình thường - nút "Rời Dòng" nhanh ở MemberDetailContent cũng chỉ là gọi update field này,
// không phải cơ chế riêng. Xoá đặt trong footer form (không đặt icon rác ngay trên dòng list - tránh
// 2 vùng bấm sát nhau trên màn hình nhỏ, xem VocationRow).
const VocationEditForm = ({member, entry, onClose, onSuccess, onDeleted, t}) => {
    const isNew = !entry?.id

    const {handleAsyncAction: doCreate, loading: creating} = useAsyncAction({
        apiInfo: familyBook_addFamilyBookMemberVocation_Api,
        onSuccess: () => {
            message.success(t('vocation updated'))
            onSuccess && onSuccess()
            onClose && onClose()
        },
        onError: () => message.error(t('error occurred')),
    })
    const {handleAsyncAction: doUpdate, loading: updating} = useAsyncAction({
        apiInfo: familyBook_updateFamilyBookMemberVocation_Api,
        onSuccess: () => {
            message.success(t('vocation updated'))
            onSuccess && onSuccess()
            onClose && onClose()
        },
        onError: () => message.error(t('error occurred')),
    })
    const {handleAsyncAction: doDelete, loading: deleting} = useAsyncAction({
        apiInfo: familyBook_deleteFamilyBookMemberVocation_Api,
        onSuccess: () => {
            message.success(t('vocation deleted'))
            onDeleted ? onDeleted() : onSuccess && onSuccess()
            onClose && onClose()
        },
        onError: () => message.error(t('error occurred')),
    })

    const initialValues = {
        vocation_type: entry?.vocation_type || ALL_VOCATION_TYPES[0],
        eff_date: entry?.eff_date ? dayjs(entry.eff_date).format('YYYY-MM-DD') : '',
        end_date: entry?.end_date ? dayjs(entry.end_date).format('YYYY-MM-DD') : '',
        source_idcode: entry?.source_idcode || '',
        religious_order: entry?.religious_order
            ? {id: entry.religious_order.id, value: entry.religious_order.name || entry.religious_order_name}
            : null,
        religious_order_name: entry?.religious_order_name || '',
        officiant_name: entry?.officiant_user?.name || entry?.officiant_name || '',
        officiant_user_id: entry?.officiant_user?.id || null,
        religious_name: entry?.religious_name || '',
    }

    const confirmDelete = () => {
        Modal.confirm({
            rootClassName: 'family-theme',
            title: t('confirm delete vocation'),
            content: t(VOCATION_LABELS[entry.vocation_type]),
            okText: t('delete'),
            okButtonProps: {danger: true},
            cancelText: t('cancel'),
            onOk: () => doDelete({}, {id: entry.id}),
        })
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values) => {
                const payload = {
                    vocation_type: values.vocation_type,
                    eff_date: values.eff_date || undefined,
                    // "" (rỗng) khác undefined (bỏ qua field) - cho phép XOÁ end_date đã set nhầm
                    // bằng cách xoá trắng field trong form, không chỉ set thêm được.
                    end_date: values.end_date || '',
                    source_idcode: values.source_idcode || undefined,
                    religious_order_id: values.religious_order?.id || undefined,
                    religious_order_name: values.religious_order?.value || values.religious_order_name || undefined,
                    officiant_user_id: values.officiant_user_id || undefined,
                    officiant_name: values.officiant_name || undefined,
                    religious_name: values.religious_name || undefined,
                }
                if (isNew) {
                    doCreate(payload, {id: member.id})
                } else {
                    doUpdate(payload, {id: entry.id})
                }
            }}>
            {({values, setFieldValue, setValues, handleSubmit, isSubmitting}) => (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="font-bold text-lg text-color-000 uppercase tracking-wide text-center">
                        {t('consecrated vocation')}
                    </div>

                    <Section title={t('vocation type')} subtitle={t('required')}>
                        {isNew ? (
                            <Select
                                size="large"
                                className="w-full"
                                value={values.vocation_type}
                                onChange={(v) => setFieldValue('vocation_type', v)}>
                                {ALL_VOCATION_TYPES.map((type) => (
                                    <Select.Option key={type} value={type}>{t(VOCATION_LABELS[type])}</Select.Option>
                                ))}
                            </Select>
                        ) : (
                            <div className="px-3 py-2 rounded-lg bg-color-50 text-sm font-semibold text-color-000">
                                {t(VOCATION_LABELS[values.vocation_type])}
                            </div>
                        )}
                    </Section>

                    {/* native <input type="date"> qua antd Input - đúng convention đã chốt cho phân
                        hệ Sổ Gia Đình, xem SacramentEditForm/AddRelativeForm. */}
                    <Section title={t('sacrament date')} subtitle={t('optional')}>
                        <Input
                            type="date"
                            size="large"
                            className="w-full"
                            value={values.eff_date}
                            onChange={(e) => setFieldValue('eff_date', e.target.value)}
                        />
                    </Section>

                    <Section title={t('belong to religious order')} subtitle={t('optional')}>
                        <LookupField
                            title={t('choose religious orders')}
                            createIfNotExists={true}
                            defaultValue={values.religious_order_name}
                            selectedOption={values.religious_order}
                            apiPath="/lookup/religious_order/get-by-types"
                            name="vocation_religious_order"
                            renderOption={renderReligiousOrderLookupOption}
                            handleChange={(e) => setValues({...values, religious_order: e.target, religious_order_name: e.target.value})}
                        />
                    </Section>

                    {/* end_date - CHỈ hiện khi SỬA (không cần hỏi ngay lúc thêm mới - hiếm khi vừa
                        thêm vừa đã kết thúc), để trống = còn đang hoạt động. Nút "Rời Dòng" nhanh ở
                        MemberDetailContent cũng set field này - vào đây sửa/xoá lại khi cần. */}
                    {!isNew && (
                        <Section title={t('left religious order on')} subtitle={t('optional')}>
                            <Input
                                type="date"
                                size="large"
                                className="w-full"
                                value={values.end_date}
                                onChange={(e) => setFieldValue('end_date', e.target.value)}
                            />
                        </Section>
                    )}

                    <Section title={t('religious name')} subtitle={t('religious name hint')}>
                        <Input
                            size="large"
                            value={values.religious_name}
                            onChange={(e) => setFieldValue('religious_name', e.target.value)}
                            placeholder={t('religious name placeholder')}
                        />
                    </Section>

                    <Section title={t('vocation officiant')} subtitle={t('optional')}>
                        <Space.Compact style={{display: 'flex', width: '100%'}}>
                            <Input
                                size="large"
                                value={values.officiant_name}
                                onChange={(e) => setValues({...values, officiant_name: e.target.value, officiant_user_id: null})}
                                allowClear
                                placeholder={t('vocation officiant placeholder')}
                                className="flex-1"
                            />
                            <UserLookupComponent
                                handleApply={(userInfo) => {
                                    if (userInfo?.id) setValues({...values, officiant_name: userInfo.name, officiant_user_id: userInfo.id})
                                }}>
                                <Button size="large" type="primary" icon={<RiSearch2Line size={14}/>} className="flex flex-center"/>
                            </UserLookupComponent>
                        </Space.Compact>
                    </Section>

                    <Section title={t('family book source code')} subtitle={t('optional')}>
                        <Input
                            size="large"
                            value={values.source_idcode}
                            onChange={(e) => setFieldValue('source_idcode', e.target.value)}
                        />
                    </Section>

                    <div className="flex items-center justify-between gap-2 pt-4 mt-1 border-t border-color-50">
                        {!isNew ? (
                            <span
                                onClick={deleting ? undefined : confirmDelete}
                                className="font-semibold text-sm text-red-500 hover:text-red-600 cursor-pointer">
                                {t('delete')}
                            </span>
                        ) : <span/>}
                        <div className="flex items-center gap-2">
                            <span
                                onClick={isSubmitting ? undefined : onClose}
                                className="font-semibold text-sm text-color-300 hover:text-color-000 cursor-pointer">
                                {t('cancel')}
                            </span>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={isSubmitting || creating || updating}
                                className="font-semibold button-rounded-lg">
                                {t('save')}
                            </Button>
                        </div>
                    </div>
                </form>
            )}
        </Formik>
    )
}

// Chip 1 bản ghi "trùng" ở sổ khác (cùng family_person_id, đã confirm qua duyệt trùng) - bấm để
// xem ngay bản ghi đó (đổi activeId nội bộ, không đóng/mở lại modal) thay vì chỉ báo số lượng.
// Phụ đề (source_idcode · tên sổ/giáo xứ) PHẢI có fallback khi sổ đó rỗng cả 3 field - nếu để trống
// trơn, 2 chip trùng TÊN người ở 2 sổ khác nhau (hợp lệ - cùng 1 người xuất hiện nhiều sổ) sẽ nhìn
// y hệt nhau, tưởng nhầm là bug hiển thị trùng lặp (bug thực tế đã gặp, xem phản hồi thực tế).
export const OtherBookChip = ({item, onClick, t}) => {
    const subtitle = [
        _.get(item, 'family_book.source_idcode'),
        _.get(item, 'family_book.title') || _.get(item, 'family_book.parish_name'),
    ].filter(Boolean).join(' · ')
    return (
        <button
            type="button"
            onClick={onClick}
            className="px-2.5 py-1.5 flex flex-col items-start gap-0 rounded-lg bg-family-50 hover:bg-family-100 border border-family-100 cursor-pointer transition-all text-left max-w-full">
            <span className="text-xs font-semibold text-family-700 truncate max-w-full">{item.name}</span>
            <span className="text-2xs text-color-400 truncate max-w-full">{subtitle || t('untitled')}</span>
        </button>
    )
}

// Quan hệ thân nhân trực hệ - Cha/Mẹ, Vợ/Chồng, Anh/Chị/Em, Con - luôn hiện đủ 4 dòng, trống thì
// báo rõ chưa cập nhật thay vì giấu dòng đi. Cha/Mẹ ưu tiên member đã liên kết trong sổ
// (father_member/mother_member), nếu chưa liên kết thì fallback qua tên tự do (father_name/
// mother_name). Export để dùng lại ở MyMemberCard (Hồ Sơ tab, FamilyBooks.js) - cùng 1 nguồn hiển
// thị quan hệ, không tự chế bản song song mỗi nơi 1 kiểu.
// Nút "+" nhanh dùng chung cho 5 hàng bên dưới (Cha/Mẹ, Chồng/Vợ, Anh/Chị/Em, Con) - cùng style
// với nút "+" đã có sẵn ở "Ơn Gọi Thánh Hiến" phía trên (bg-family-100), giữ nhất quán trong cùng
// 1 popup thay vì tự chế icon/màu riêng cho từng chỗ. w-6 h-6 (kích thước CỐ ĐỊNH) + flex centering
// thay vì chỉ dựa vào padding quanh icon - padding đơn thuần (p-1/p-1.5) bị line-height của text
// bao quanh (Section/hàng chữ) kéo cao hơn bề ngang, ra hình chữ nhật đứng thay vì vuông (bug thực
// tế đã gặp) - kích thước cố định không phụ thuộc line-height context xung quanh nữa.
const AddRelativeQuickButton = ({title, modalProps, onSuccess, t}) => (
    <DrawDialog
        width={480}
        wrapClassName="family-theme"
        component={
            <button
                type="button"
                title={title}
                className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-lg bg-family-100 text-family-700 hover:bg-family-200 cursor-pointer">
                <FaPlus size={10}/>
            </button>
        }
        footer={false}>
        {(isToggle, toggle) => isToggle && (
            <AddRelativeModal {...modalProps} onClose={() => toggle(false)} onSuccess={onSuccess} t={t}/>
        )}
    </DrawDialog>
)

export const RelativesCard = ({item, onSuccess, t}) => {
    const fatherMember = _.get(item, 'father_member')
    const motherMember = _.get(item, 'mother_member')
    const spouse = _.get(item, 'spouse')
    const siblings = _.get(item, 'siblings', [])
    const children = _.get(item, 'children', [])
    const familyBookId = _.get(item, 'family_book.id')

    // Chồng/Vợ - còn TRỐNG mới cho thêm (mô hình chỉ 1 vợ/chồng "direct" duy nhất, xem
    // FamilyBookMember::spouseMember()) - defaultRelationshipType theo giới tính CỦA CHÍNH item
    // (đối phương cần thêm là giới ngược lại), cùng quy ước nhãn "Vợ"/"Chồng" đã tính ở trên.
    const spouseSentinel = item.gender === 2 ? 'SPOUSE_HUSBAND' : 'SPOUSE_WIFE'

    // "+" Cha/Mẹ (tách sổ riêng, xem FamilyBookController::addParentBook()) - CHỈ có nghĩa khi item
    // là gia trưởng hoặc vợ/chồng gia trưởng của CHÍNH sổ này (splitOffParent() cần anchorMember là
    // 1 trong 2 vai trò đó) - "Con" trong sổ thường đã tự suy ra Cha/Mẹ = gia trưởng/vợ-chồng gia
    // trưởng của sổ (effectiveFatherMember/effectiveMotherMember), không rơi vào ca "Chưa có dữ
    // liệu" này. forcedForMember truyền thẳng theo relationship_type CỦA CHÍNH item - đã biết chắc
    // "của ai", khỏi cần hỏi lại qua canChooseForMember (xem AddRelativeModal).
    const canAddParents = item.relationship_type === 'HEAD' || item.relationship_type === 'SPOUSE'
    const forcedForMember = item.relationship_type === 'SPOUSE' ? 'SPOUSE' : 'HEAD'

    return (
        <div className="p-3 flex flex-col gap-1.5 bg-color-50 border border-color-100 rounded-xl">
            <span className="font-bold text-sm text-color-000">{t('direct relatives')}</span>
            <div className="flex items-center gap-1.5 text-sm text-color-200">
                <span>
                    {t('father')}: {fatherMember?.name || item.father_name
                        ? <span className="font-semibold text-color-000">{fatherMember?.name || item.father_name}</span>
                        : <span className="italic text-color-300">{t('not provided yet')}</span>
                    }
                </span>
                {item.edit && canAddParents && !fatherMember?.name && !item.father_name && (
                    <AddRelativeQuickButton
                        title={t('add father')}
                        modalProps={{
                            familyBookId,
                            defaultRelationshipType: 'FATHER',
                            lockRelationship: true,
                            forcedForMember,
                        }}
                        onSuccess={onSuccess}
                        t={t}/>
                )}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-color-200">
                <span>
                    {t('mother')}: {motherMember?.name || item.mother_name
                        ? <span className="font-semibold text-color-000">{motherMember?.name || item.mother_name}</span>
                        : <span className="italic text-color-300">{t('not provided yet')}</span>
                    }
                </span>
                {item.edit && canAddParents && !motherMember?.name && !item.mother_name && (
                    <AddRelativeQuickButton
                        title={t('add mother')}
                        modalProps={{
                            familyBookId,
                            defaultRelationshipType: 'MOTHER',
                            lockRelationship: true,
                            forcedForMember,
                        }}
                        onSuccess={onSuccess}
                        t={t}/>
                )}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-color-200">
                <span>
                    {/* Nhãn theo giới tính CỦA CHÍNH item (không phải giới tính đối phương) - item nam
                        thì đối phương là "Vợ", item nữ thì đối phương là "Chồng". Tái dùng key có sẵn
                        relationship_husband/wife (đã dùng cho vai trò riêng item ở MemberRow,
                        FamilyBookDetail.js) - ở đây ngược hướng vì đang mô tả người phối ngẫu, không
                        phải chính item. */}
                    {t(item.gender === 1 ? 'relationship_wife' : item.gender === 2 ? 'relationship_husband' : 'relationship_spouse')}: {spouse?.name
                        ? <span className="font-semibold text-color-000">{spouse.name}</span>
                        : <span className="italic text-color-300">{t('not provided yet')}</span>
                    }
                </span>
                {item.edit && !spouse && (
                    <AddRelativeQuickButton
                        title={t('add spouse')}
                        modalProps={{
                            anchorMemberId: item.id,
                            anchorMemberName: item.name,
                            defaultRelationshipType: spouseSentinel,
                            lockRelationship: true,
                        }}
                        onSuccess={onSuccess}
                        t={t}/>
                )}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-color-200">
                <span>
                    {t('relationship_sibling')}{siblings.length > 0 ? ` (${siblings.length})` : ''}: {siblings.length > 0
                        ? <span className="font-semibold text-color-000">{siblings.map((s) => s.name).join(', ')}</span>
                        : <span className="italic text-color-300">{t('not provided yet')}</span>
                    }
                </span>
                {/* Anh/Chị/Em - thêm THẲNG vào familyBookId hiện có (không cần anchorMemberId/tạo sổ,
                    KHÁC Chồng/Con bên dưới) - item đang xem đã ở sẵn trong sổ này (relationship_type
                    CHILD, cùng cha/mẹ đã hiện phía trên) nên "anh/chị/em" luôn có cùng sổ. */}
                {item.edit && familyBookId && (
                    <AddRelativeQuickButton
                        title={t('add sibling')}
                        modalProps={{
                            familyBookId,
                            defaultRelationshipType: 'CHILD',
                            lockRelationship: true,
                        }}
                        onSuccess={onSuccess}
                        t={t}/>
                )}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-color-200">
                <span>
                    {t('relationship_child')}{children.length > 0 ? ` (${children.length})` : ''}: {children.length > 0
                        ? <span className="font-semibold text-color-000">{children.map((c) => c.name).join(', ')}</span>
                        : <span className="italic text-color-300">{t('not provided yet')}</span>
                    }
                </span>
                {item.edit && (
                    <AddRelativeQuickButton
                        title={t('add child')}
                        modalProps={{
                            anchorMemberId: item.id,
                            anchorMemberName: item.name,
                            defaultRelationshipType: 'CHILD',
                            lockRelationship: true,
                        }}
                        onSuccess={onSuccess}
                        t={t}/>
                )}
            </div>
        </div>
    )
}

// viewOnly - ÉP xem-mới-được dù API trả item.edit=true (org admin quản lý sổ chính org sở hữu vẫn
// có quyền sửa bí tích, xem canEditFamilyBook() BE). Workspace > Tra cứu Giáo dân/Tra cứu bí tích
// dùng cờ này - khu vực đó chỉ để TÌM/XEM nhanh, sửa thật vẫn phải qua đúng luồng quản lý sổ
// (FamilyBookDetail.js), tránh sửa nhầm ngay trong kết quả tìm kiếm. Chỗ gọi cũ (trang cá nhân,
// FamilyBooks.js/FamilyBookDetail.js) không truyền prop này nên hành vi giữ nguyên như trước.
const MemberDetailContent = ({memberId, viewOnly, t}) => {
    // activeId: cho phép bấm chip "cũng xuất hiện ở sổ khác" để xem thẳng bản ghi kia trong cùng
    // modal (không remount lại DrawDialog từ ngoài) - khởi tạo theo memberId ban đầu, tự reset khi
    // đổi memberId prop (parent remount qua key={memberId} nên effect này chỉ cần cho trường hợp
    // đổi activeId nội bộ không kèm remount).
    const [activeId, setActiveId] = useState(memberId)
    useEffect(() => { setActiveId(memberId) }, [memberId])
    // Đổi để buộc gọi lại API sau khi sửa 1 bí tích/hồ sơ (useAsync cache theo deps, đổi
    // refreshToken là đủ, không cần remount cả modal).
    const [refreshToken, setRefreshToken] = useState(0)
    // Sửa nhanh hồ sơ (ngày sinh/nghề nghiệp/địa chỉ/tên thánh...) NGAY tại đây thay vì phải qua
    // trang "Sổ Gia Đình" - tái dùng ĐÚNG form/API đã có (AddRelativeModal, familyBook_
    // updateFamilyBookMember_Api), không tự chế form rút gọn song song.
    const [editing, setEditing] = useState(false)
    // "Rời Dòng" - hook phải khai báo TRƯỚC early-return bên dưới (rule of hooks), dùng ở
    // confirmDepart phía dưới `item` đã sẵn sàng. Chỉ set `end_date` NGAY TRÊN row đang chọn qua
    // API update sẵn có - không tạo row mới, xem giải thích ở ALL_VOCATION_TYPES.
    const {handleAsyncAction: doDepart} = useAsyncAction({
        apiInfo: familyBook_updateFamilyBookMemberVocation_Api,
        onSuccess: () => {
            message.success(t('vocation updated'))
            setRefreshToken((c) => c + 1)
        },
        onError: () => message.error(t('error occurred')),
    })

    const {response, isLoading} = useAsync({
        apiInfo: familyBookMember_getById_Api,
        query: {id: activeId},
        deps: [activeId, refreshToken],
    })
    const rawItem = _.get(response, 'data')

    if (isLoading && !rawItem) {
        return (
            <div className="flex items-center justify-center py-10">
                <LoadingOutlined className="text-family-500"/>
            </div>
        )
    }
    if (!rawItem) return null
    // Ép edit=false khi viewOnly - MỌI nhánh bên dưới (nút sửa hồ sơ, ô bí tích, Ơn Gọi Thánh Hiến,
    // nút "+" thân nhân ở RelativesCard) đều đọc item.edit, nên chỉ cần chặn ở 1 chỗ duy nhất thay
    // vì thread thêm prop viewOnly xuống từng nơi.
    const item = viewOnly ? {...rawItem, edit: false} : rawItem

    const familyBook = _.get(item, 'family_book')
    const sacraments = _.get(item, 'sacraments', [])
    const vocations = _.get(item, 'vocations', [])
    const others = _.get(item, 'others', [])
    // computeAge tính tuổi lúc mất (death_date) cho người đã mất, thay vì tuổi-nếu-còn-sống-tới-hôm-nay
    const age = computeAge(item.birth_date, item.death_date)

    // Đã mất -> kèm dấu ✝ ngay sau số tuổi (tuổi lúc mất, không phải tuổi-nếu-còn-sống) - quy ước
    // gia phả chuẩn, thay cho chữ "Đã mất" tách rời.
    const birthDisplay = item.birth_date
        ? `${age} ${t('years old')}${item.death_date ? ' ✝' : ''} (${dayjs(item.birth_date).format('DD/MM/YYYY')})`
        : null

    // "Rời Dòng" - hành động NHANH gắn liền với ĐÚNG 1 dòng khấn đang hoạt động (entry.end_date còn
    // trống) - set end_date = hôm nay ngay trên row đó, có confirm trước (hành động nhạy cảm, không
    // thể "click nhầm"). Muốn sửa lại ngày rời/xoá rời dòng thì bấm vào dòng để mở form đầy đủ.
    const confirmDepart = (entry) => {
        Modal.confirm({
            rootClassName: 'family-theme',
            title: t('confirm leave religious order'),
            content: [entry.religious_order_name, t(VOCATION_LABELS[entry.vocation_type])].filter(Boolean).join(' · '),
            okText: t('leave religious order'),
            okButtonProps: {danger: true},
            cancelText: t('cancel'),
            onOk: () => doDepart({end_date: dayjs().format('YYYY-MM-DD')}, {id: entry.id}),
        })
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-0.5">
                <span className="text-2xs font-bold text-family-600 uppercase tracking-widest">{t('member profile')}</span>
                {/* Icon giới tính đặt SAU tên (đồng nhất với các nơi khác trong Cây Gia Phả/list
                    thành viên) - trước đây đặt trước tên, không nhất quán. */}
                <div className="flex items-center gap-2 flex-wrap">
                    {item.patron_saint_name && (
                        <span className="font-bold text-lg text-family-600 leading-snug">{item.patron_saint_name}</span>
                    )}
                    <span className="font-bold text-lg text-color-000 leading-snug">{item.name}</span>
                    <GenderIcon gender={item.gender} size={18} className="text-color-300 flex-shrink-0"/>
                    {item.death_date && (
                        <span className="text-lg text-color-300 leading-snug" title={t('deceased')}>✝</span>
                    )}
                    {item.is_priest && <PriestBadge/>}
                    {item.is_religious && <ReligiousBadge/>}
                </div>
            </div>

            {/* Thông tin cơ bản - card nền + viền rõ ràng để tạo nhóm thị giác, LUÔN hiện đủ field.
                Nghề nghiệp/địa chỉ để riêng dòng (không ghép cột với ngày sinh) vì data có thể dài.
                RIÊNG ngày mất: đây là thông tin nhạy cảm - CHỈ hiện khi thực sự đã mất, không áp
                dụng quy tắc "luôn hiện label" chung của các field khác (không hỏi/ngụ ý người còn
                sống là "chưa có dữ liệu mất"). */}
            <div className="relative p-3 flex flex-col gap-3 bg-family-50 border border-family-100 rounded-xl">
                {/* Sửa hồ sơ - chỉ hiện khi có quyền (item.edit, cùng cờ đang dùng cho sửa bí tích
                    bên dưới), góc trên-phải để không chen vào field nào. */}
                {item.edit && (
                    <button
                        type="button"
                        onClick={() => setEditing(true)}
                        title={t('edit relative')}
                        className="absolute top-3 right-3 p-1.5 rounded-lg bg-white border border-family-200 text-family-700 hover:bg-family-100 cursor-pointer">
                        <IoPencilOutline size={14}/>
                    </button>
                )}
                <div className={classNames('grid gap-3', item.death_date ? 'grid-cols-2' : 'grid-cols-1')}>
                    <InfoField label={t('age / date of birth')} value={birthDisplay} t={t}/>
                    {item.death_date && (
                        <InfoField label={t('deceased')} value={dayjs(item.death_date).format('DD/MM/YYYY')} t={t}/>
                    )}
                </div>
                <InfoField label={t('occupation')} value={item.occupation} t={t}/>
                <InfoField label={t('current address')} value={item.current_address} t={t}/>
                <InfoField label={t('parish')} value={familyBook?.parish_name} t={t}/>
                {/* Liên hệ - item.contact_email/contact_phone đã resolve sẵn ở BE (ưu tiên tài khoản
                    liên kết nếu có), bấm gọi/gửi mail được ngay (xem ContactField). BE trả về null
                    khi người xem không có quyền (can_manage_contact=false) - báo đúng lý do thay vì
                    lẫn với "chưa khai". */}
                <ContactField
                    label={t('contact email')} value={item.contact_email} href={`mailto:${item.contact_email}`}
                    hidden={item.can_manage_contact === false} t={t}
                />
                <ContactField
                    label={t('contact phone')} value={item.contact_phone} href={`tel:${item.contact_phone}`}
                    hidden={item.can_manage_contact === false} t={t}
                />

                {/* Đã liên kết tài khoản thật (member_user_id) - hiện link tới hồ sơ + email (đã che
                    - UserTransformerPublic/TextHelper::hideEmail, không lộ email đầy đủ cho bất kỳ
                    ai xem được sổ này). Không có tài khoản (chỉ khai tên tự do) -> ẩn hẳn dòng này,
                    không hỏi thừa "chưa có dữ liệu tài khoản". */}
                {item.member_user && (
                    <div className="flex items-center gap-2 pt-2 border-t border-family-100">
                        <Avatar
                            size={28}
                            src={getAvatar(item.member_user)}
                            icon={<BsPersonFill size={13} className="text-color-500"/>}
                            className="flex-shrink-0 flex flex-center background-200"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="text-2xs font-semibold text-color-400 uppercase tracking-wider">{t('linked account')}</div>
                            <Link
                                to={getLinkToDetail(item.member_user)}
                                target="_blank"
                                className="text-sm font-medium text-family-700 no-underline hover:underline truncate block">
                                {getTitle(item.member_user)}
                            </Link>
                            {item.member_user.email && (
                                <span className="text-xs text-color-400 italic truncate block">{item.member_user.email}</span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Bí tích - luôn đủ 6 ô, ô chưa lãnh nhận hiện mờ (inactive), không giấu. Có quyền sửa
                (item.edit) -> bấm 1 ô mở form sửa nhanh ĐÚNG bí tích đó (ngày + giáo xứ), gọi thẳng
                familyBook_updateFamilyBookMember_Api - BE đã sẵn, xem SacramentEditForm.
                sacraments_locked (family_book, giáo xứ khoá sau khi xác nhận liên kết - xem
                FamilyBookController::changeFamilyBookParishLinkStatus()) -> cũng khoá NGAY Ở ĐÂY dù
                item.edit=true, BE đã chặn write rồi nhưng vẫn nên disable UI cho rõ thay vì để user
                bấm sửa xong nhận lỗi.

                Sổ đã được giáo xứ xác nhận liên kết (family_book.status===1) thì giáo xứ LUÔN sửa
                được bí tích (kể cả KHÔNG khoá - Workspace gọi bypassSacramentsLock=true, xem
                FamilyBookMember::edit()) - khoá/mở CHỈ quyết định CHÍNH chủ sổ có được tự sửa hay
                không, không quyết định quyền của giáo xứ. Phải nói rõ cả 2 trạng thái (không chỉ lúc
                khoá) - nếu chỉ báo lúc khoá, user dễ hiểu lầm "mở khoá = chỉ mình tôi sửa được", đúng
                phản hồi thực tế cần tránh hiểu lầm này. */}
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5">
                    <span className="font-bold text-sm text-color-000">✝ {t('sacraments received')}</span>
                    {item.family_book?.sacraments_locked && (
                        <span className="px-1.5 py-0.5 flex items-center gap-1 rounded text-2xs font-semibold bg-color-50 text-color-400">
                            <IoLockClosedOutline size={11}/>
                            {t('sacraments locked')}
                        </span>
                    )}
                </div>
                {/* Diễn giải ĐẦY ĐỦ luôn hiện trên màn hình (không chỉ 1 badge nhỏ + tooltip hover -
                    dễ bị bỏ sót, nhất là trên mobile không có hover) - dùng chung InfoBanner với
                    MomentsPanel/CreateFamilyBookForm, đúng convention banner giải thích đã có. */}
                {item.family_book?.sacraments_locked ? (
                    <InfoBanner>{t('sacraments locked by parish hint')}</InfoBanner>
                ) : item.family_book?.status === 1 && (
                    <InfoBanner>{t('sacraments unlocked but parish can edit hint')}</InfoBanner>
                )}
                <div className="grid grid-cols-2 gap-2">
                    {ALL_SACRAMENT_TYPES.map((type) => {
                        const entry = sacraments.find((s) => s.sacrament_type === type)
                        if (!item.edit || item.family_book?.sacraments_locked) {
                            return <SacramentSlot key={type} type={type} entry={entry} t={t}/>
                        }
                        return (
                            <DrawDialog
                                key={type}
                                width={420}
                                wrapClassName="family-theme"
                                component={<SacramentSlot type={type} entry={entry} t={t}/>}
                                footer={false}>
                                {(isToggle, toggle) => isToggle && (
                                    <SacramentEditForm
                                        member={item}
                                        type={type}
                                        entry={entry}
                                        onClose={() => toggle(false)}
                                        onSuccess={() => setRefreshToken((c) => c + 1)}
                                        t={t}
                                    />
                                )}
                            </DrawDialog>
                        )
                    })}
                </div>
            </div>

            {/* Ơn Gọi Thánh Hiến - KHÁC bí tích ở trên: chỉ liên quan thiểu số thành viên là tu sĩ VÀ
                có thể nhiều dòng theo thời gian (chuyển dòng) nên hiện dạng LIST + nút "+" thêm mới
                (chọn loại NGAY TRONG form, không phải chọn từ grid cố định như bí tích) thay vì grid
                slot cố định. CHỈ hiện cả block khi đã có dữ liệu (member khác xem cũng thấy) hoặc
                đang có quyền sửa (chủ sổ thấy để có thể thêm). */}
            {(item.edit || vocations.length > 0) && (
                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-sm text-color-000">🕊 {t('consecrated vocation')}</span>
                        {item.edit && (
                            <DrawDialog
                                width={420}
                                wrapClassName="family-theme"
                                component={
                                    <button
                                        type="button"
                                        title={t('add vocation')}
                                        // w-6 h-6 cố định thay vì chỉ dựa padding - xem giải thích ở
                                        // AddRelativeQuickButton (bug "chữ nhật đứng" đã gặp).
                                        className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-lg bg-family-100 text-family-700 hover:bg-family-200 cursor-pointer">
                                        <FaPlus size={10}/>
                                    </button>
                                }
                                footer={false}>
                                {(isToggle, toggle) => isToggle && (
                                    <VocationEditForm
                                        member={item}
                                        entry={null}
                                        onClose={() => toggle(false)}
                                        onSuccess={() => setRefreshToken((c) => c + 1)}
                                        t={t}
                                    />
                                )}
                            </DrawDialog>
                        )}
                    </div>
                    {!vocations.length && (
                        <span className="text-xs italic text-color-300">{t('vocation not recorded')}</span>
                    )}
                    <div className="flex flex-col gap-2">
                        {vocations.map((entry) => {
                            // Nút "Rời Dòng" - CHỈ hiện trên dòng đang hoạt động (chưa có end_date)
                            // và đang có quyền sửa.
                            const canDepart = item.edit && !entry.end_date
                            const actions = canDepart && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); confirmDepart(entry) }}
                                    title={t('leave religious order')}
                                    className="px-2 py-1 rounded-lg text-2xs font-semibold text-red-500 hover:bg-red-50 cursor-pointer flex-shrink-0 whitespace-no-wrap">
                                    {t('leave religious order')}
                                </button>
                            )
                            if (!item.edit) {
                                return <VocationRow key={entry.id} entry={entry} actions={actions} t={t}/>
                            }
                            return (
                                <DrawDialog
                                    key={entry.id}
                                    width={420}
                                    wrapClassName="family-theme"
                                    component={<VocationRow entry={entry} actions={actions} t={t}/>}
                                    footer={false}>
                                    {(isToggle, toggle) => isToggle && (
                                        <VocationEditForm
                                            member={item}
                                            entry={entry}
                                            onClose={() => toggle(false)}
                                            onSuccess={() => setRefreshToken((c) => c + 1)}
                                            t={t}
                                        />
                                    )}
                                </DrawDialog>
                            )
                        })}
                    </div>
                </div>
            )}

            <RelativesCard item={item} onSuccess={() => setRefreshToken((c) => c + 1)} t={t}/>

            {/* Cùng 1 người thật (family_person_id) nhưng xuất hiện ở >1 sổ (đã duyệt trùng) - mỗi
                sổ có thể khai chi tiết khác nhau nên không gộp, bấm chip để xem đúng bản ghi đó. */}
            {others.length > 0 && (
                <div className="flex flex-col gap-2">
                    <span className="font-bold text-sm text-color-000">{t('also appears in other family books')}</span>
                    <div className="flex flex-wrap gap-1.5">
                        {others.map((o) => (
                            <OtherBookChip key={o.id} item={o} onClick={() => setActiveId(o.id)} t={t}/>
                        ))}
                    </div>
                </div>
            )}

            {familyBook?.title && (
                <Link
                    to={getLinkToDetail(familyBook)}
                    className="px-3 py-2.5 flex items-center justify-between gap-2 rounded-lg bg-family-600 hover:bg-family-700 no-underline transition-all">
                    <span className="flex items-center gap-2 min-w-0">
                        <IoLibraryOutline size={16} className="text-white flex-shrink-0"/>
                        <span className="text-sm font-semibold text-white truncate">{familyBook.title}</span>
                    </span>
                    <IoChevronForward size={16} className="text-white flex-shrink-0"/>
                </Link>
            )}

            {editing && (
                <AddRelativeModal
                    familyBookId={item.family_book_id || familyBook?.id}
                    editingMember={item}
                    onClose={() => setEditing(false)}
                    onSuccess={() => {
                        setEditing(false)
                        setRefreshToken((c) => c + 1)
                    }}
                    t={t}
                />
            )}
        </div>
    )
}

// Hồ sơ 1 thành viên - mở từ bất kỳ đâu (bấm 1 sự kiện kỷ niệm ở Trang Chủ, 1 thẻ thành viên...).
// Dùng DrawDialog chuẩn của dự án (mobile: bottom-sheet kéo lên, desktop: modal căn giữa) thay vì
// tự dựng Modal riêng. DrawDialog chỉ nhận `visible` làm defaultValue cho Toggle nội bộ, không phải
// prop điều khiển được liên tục - nên component này chỉ mount khi có memberId (parent bọc bằng
// `{memberId && <MemberDetailModal key={memberId} memberId={memberId} .../>}`), mount mới mỗi lần
// đổi memberId -> Toggle khởi tạo visible=true ngay, mở đúng lúc.
const MemberDetailModal = ({memberId, viewOnly, onClose}) => {
    const t = useTranslate()
    if (!memberId) return null

    return (
        <DrawDialog
            visible={true}
            width={560}
            wrapClassName="family-theme"
            component={<span style={{display: 'none'}}/>}
            onCancel={onClose}
            footer={false}>
            {(isToggle) => isToggle && <MemberDetailContent memberId={memberId} viewOnly={viewOnly} t={t}/>}
        </DrawDialog>
    )
}

export default MemberDetailModal
